package org.matthewo.pages.event

import io.ktor.resources.*
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import org.matthewo.routes.API
import web.events.EventHandler
import web.sse.EventSource

// open connections by "year/code", shared by every component showing that event
private val connections = mutableMapOf<String, EventConnection>()

/**
 * Emits each time the event's data changes on the server. Collectors of the same event share one connection,
 * which closes when the last of them stops.
 */
fun eventChanges(year: Int, code: String): Flow<Unit> = flow {
    val key = "$year/$code"
    val connection = connections.getOrPut(key) { EventConnection(year, code) }
    connection.watchers++
    try {
        emitAll(connection.changes)
    } finally {
        if (0 == --connection.watchers) {
            connection.source.close()
            connections.remove(key)
        }
    }
}

private class EventConnection(year: Int, code: String) {
    val source = EventSource(href(RESOURCES_FORMAT, API.Event.Updates(year, code)))
    val changes = MutableSharedFlow<Unit>(extraBufferCapacity = 1, onBufferOverflow = BufferOverflow.DROP_OLDEST)
    var watchers = 0

    // the first version received is what was just loaded, so only later ones are changes;
    // the server sends the version again after the browser reconnects, which catches changes missed while disconnected
    private var version: String? = null

    init {
        source.onmessage = EventHandler { message ->
            val received = message.data.toString()
            if (null != version && received != version) changes.tryEmit(Unit)
            version = received
        }
    }
}
